import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';

/**
 * Phase 14a follow-up — admin sign-out.
 *
 * Mirrors apps/portal/src/auth/sign-out.ts: POST /auth/sign-out revokes
 * the session server-side + Set-Cookie clears the httpOnly cookie.
 *
 * Phase 14a audit follow-up (H2): both onSuccess AND onError clear the
 * client-side query cache. If the backend POST fails (transport drop),
 * leaving the operator in a half-signed-out state — admin UI still
 * shows them as signed in even though they explicitly clicked "Sign
 * out" — is worse UX than the cache-only client clear. Server-side
 * session may persist briefly (TTLs out normally) but the client at
 * least stops trusting the previous identity.
 *
 * Caller (AdminLayout) navigates the operator to the portal sign-in
 * URL on both success and error so they end up at the same place.
 */
export function useSignOut() {
  const queryClient = useQueryClient();
  // Phase 17 audit fix (2026-07-15) — clear the ENTIRE query cache on
  // sign-out rather than only the ['auth'] + ['admin'] prefixes. Admin
  // pages cache operator-facing PII under other keys; selective removal
  // could leave those live in the race between the mutation resolving and
  // the AdminLayout navigation. clear() drops everything — nothing in the
  // cache is worth preserving across an identity change.
  const clearLocalAuth = () => {
    queryClient.clear();
  };
  return useMutation({
    mutationFn: async () => {
      await api.POST('/api/v1/auth/sign-out');
    },
    onSuccess: clearLocalAuth,
    onError: clearLocalAuth,
  });
}